import React, { useEffect, useMemo } from 'react';
import { ConfigProvider, theme as antTheme, ThemeConfig } from 'antd';
import { useTheme } from '../../context/ThemeContext';
import { createThemeConfig } from './antd-theme';
import { mergeWithKoodookaTheme } from '../../features/insurance/utils/theme';
import type { ThemeDefinition } from './themes/types';

/**
 * Build map of CSS custom properties from merged Ant Design + Insurance tokens.
 * When a ThemeDefinition is provided its tokens take precedence over defaults.
 */
type TokenMap = Record<string, unknown>;

function tokenString(tokens: TokenMap, key: string): string | undefined {
    const value = tokens[key];
    return typeof value === 'string' ? value : undefined;
}

function buildCssVarMap(tokens: TokenMap, isDark: boolean, themeDef: ThemeDefinition): Record<string, string> {
    const td = themeDef.tokens;
    const primary = td.colorPrimary || tokenString(tokens, 'colorPrimary') || '#9E321F';
    const primaryOnSidebar = td.colorPrimaryOnSidebar || tokenString(tokens, 'colorPrimaryOnSidebar') || '#CA5A43';
    const primaryHover = td.colorPrimaryHover || tokenString(tokens, 'colorPrimaryHover') || td.colorAccent;
    const primaryActive = td.colorPrimaryActive || tokenString(tokens, 'colorPrimaryActive') || primary;
    const primaryBg = td.colorPrimaryBg || tokenString(tokens, 'colorPrimaryBg') || `rgba(${parseHexToRgb(primary) || '158, 50, 31'}, 0.12)`;
    const focusColor = td.colorFocus || primaryHover;
    const bgBase = td.colorBgBase || tokenString(tokens, 'colorBgBase') || (isDark ? '#0B0B0C' : '#F4F0EA');
    const bgContainer = td.colorBgContainer || tokenString(tokens, 'colorBgContainer') || (isDark ? '#171617' : '#FAF8F5');
    const bgElevated = td.colorBgElevated || tokenString(tokens, 'colorBgElevated') || bgContainer;
    const chromeBackground = td.colorBgSidebar || '#0B0B0C';
    const chromeBackgroundRgb = parseHexToRgb(chromeBackground) || '11, 11, 12';
    const chromeText = td.colorTextOnChrome || '#FAF8F5';
    const chromeTextRgb = parseHexToRgb(chromeText) || '250, 248, 245';
    const chromeOpacity = 0.78;
    const chromeBackgroundValue = `rgba(${chromeBackgroundRgb}, ${chromeOpacity})`;
    const chromeBorder = `rgba(${chromeTextRgb}, 0.18)`;
    const chromeShadow = `0 8px 32px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(${chromeTextRgb}, 0.12), inset 0 -1px 0 rgba(0, 0, 0, 0.22)`;
    const success = td.colorSuccess || tokenString(tokens, 'colorSuccess') || '#16805A';
    const warning = td.colorWarning || tokenString(tokens, 'colorWarning') || '#B7791F';
    const error = td.colorError || tokenString(tokens, 'colorError') || '#C53030';
    const info = td.colorInfo || tokenString(tokens, 'colorInfo') || '#2563EB';
    const rgba = (color: string, alpha: number) => {
        const rgb = parseHexToRgb(color);
        return rgb ? `rgba(${rgb}, ${alpha})` : color;
    };
    const pick = (key: string, fallback?: string) => tokenString(tokens, key) || fallback || '';

    return {
        // ===================
        // Theme palette
        // ===================

        // Primary colors
        '--color-primary': primary,
        '--color-primary-on-sidebar': primaryOnSidebar,
        '--color-primary-hover': primaryHover,
        '--color-primary-active': primaryActive,
        '--color-primary-bg': primaryBg,
        '--color-primary-rgb': parseHexToRgb(primary) || '',
        '--color-primary-hover-rgb': parseHexToRgb(primaryHover) || '',
        '--color-primary-active-rgb': parseHexToRgb(primaryActive) || '',
        '--color-primary-bg-rgb': parseHexToRgb(primaryBg) || '',

        // Text colors
        '--color-text-primary': td.colorText,
        '--color-text-secondary': td.colorTextSecondary,
        '--color-text-tertiary': td.colorTextTertiary,
        '--color-text-on-chrome': chromeText,
        '--color-text-on-chrome-rgb': chromeTextRgb,
        '--color-text-inverse': td.colorTextInverse || (isDark ? td.colorBgBase : td.colorBgContainer),

        // Backgrounds
        '--color-bg-base': bgBase,
        '--color-bg-base-rgb': parseHexToRgb(bgBase) || '',
        '--color-bg-container': bgContainer,
        '--color-bg-container-rgb': parseHexToRgb(bgContainer) || '',
        '--color-bg-elevated': bgElevated,
        '--color-bg-elevated-rgb': parseHexToRgb(bgElevated) || '',
        '--color-bg-layout': bgBase,
        '--color-bg-gradient-mid': primaryBg,
        '--color-bg-spotlight': bgElevated,
        '--color-bg-mask': rgba(bgBase, isDark ? 0.82 : 0.35),

        // Mesh gradient accents for the active theme
        '--color-mesh-accent-1': rgba(primaryHover, isDark ? 0.10 : 0.06),
        '--color-mesh-accent-2': rgba(td.colorAccent, isDark ? 0.08 : 0.05),
        '--color-mesh-accent-3': rgba(td.colorSuccess, isDark ? 0.06 : 0.04),

        // Borders
        '--color-border': td.colorBorder,
        '--color-border-secondary': td.colorBorderSecondary,
        '--color-border-hover': tokenString(tokens, 'colorBorderHover') || td.colorBorderSecondary,

        // Status colors - Refined, not harsh
        '--color-success': success,
        '--color-success-bg': rgba(success, isDark ? 0.18 : 0.10),
        '--color-success-border': rgba(success, isDark ? 0.45 : 0.35),
        '--color-warning': warning,
        '--color-warning-bg': rgba(warning, isDark ? 0.18 : 0.10),
        '--color-warning-border': rgba(warning, isDark ? 0.45 : 0.35),
        '--color-error': error,
        '--color-error-bg': rgba(error, isDark ? 0.18 : 0.10),
        '--color-error-border': rgba(error, isDark ? 0.45 : 0.35),
        '--color-info': info,
        '--color-info-bg': rgba(info, isDark ? 0.18 : 0.10),
        '--color-info-border': rgba(info, isDark ? 0.45 : 0.35),

        // Banking specific
        '--color-profit': success,
        '--color-loss': error,
        '--color-pending': warning,
        '--color-frozen': td.colorTextTertiary,

        // Premium accent - Gold
        '--color-gold': '#B8934E',                                   // Antique Gold
        '--color-gold-bg': isDark ? '#2E2815' : '#F7F3E8',          // Deep gold / Champagne
        '--color-gold-border': isDark ? '#8F7139' : '#D4C4A0',

        // Shadows
        '--shadow-sm': td.cardShadow,
        '--shadow-md': td.boxShadow,
        '--shadow-lg': td.boxShadowSecondary,
        '--shadow-xl': td.boxShadowSecondary,

        // Border radius
        '--radius-sm': '4px',
        '--radius-base': '8px',
        '--radius-md': '12px',
        '--radius-lg': '16px',
        '--radius-xl': '24px',

        // Spacing (based on 8px grid)
        '--spacing-xs': '4px',
        '--spacing-sm': '8px',
        '--spacing-md': '16px',
        '--spacing-lg': '24px',
        '--spacing-xl': '32px',
        '--spacing-2xl': '48px',

        // Transitions
        '--transition-fast': '150ms ease-in-out',
        '--transition-base': '200ms ease-in-out',
        '--transition-slow': '300ms ease-in-out',

        // ===================
        // Glass Effect Tokens
        // ===================
        '--glass-bg-opacity': td.glassOpacity,
        '--glass-navbar-opacity': isDark ? '0.18' : '0.75',
        '--glass-blur': td.glassBlur,
        '--glass-saturation': td.glassSaturation,
        '--glass-brightness': isDark ? '1.2' : '1.08',
        '--glass-border-opacity': isDark ? '0.12' : '0.25',
        '--glass-border-highlight': isDark ? '0.18' : '0.4',
        '--glass-border-highlight-secondary': isDark ? '0.12' : '0.3',
        '--glass-shadow': `0 8px 32px ${rgba(primary, isDark ? 0.20 : 0.08)}, 0 0 0 0.5px ${rgba(td.colorText, isDark ? 0.08 : 0.12)}`,
        '--glass-inner-glow': `inset 0 1px 0 ${rgba(td.colorBgElevated, isDark ? 0.10 : 0.50)}`,
        '--glass-inner-shadow': `inset 0 -1px 0 ${rgba(bgBase, isDark ? 0.22 : 0.06)}`,

        // Premium glass card tokens (SmartCard)
        '--glass-card-opacity': isDark ? '0.03' : '0.01',
        '--glass-card-tint-start': isDark ? '0.04' : '0.08',
        '--glass-card-tint-end': isDark ? '0.02' : '0.03',
        '--glass-card-shadow-alpha': isDark ? '0.20' : '0.05',
        '--glass-card-shadow-hover': isDark ? '0.30' : '0.10',
        '--glass-card-glow': isDark ? '0.05' : '0.12',

        // Glass modal tokens
        '--glass-content-opacity': td.glassContentOpacity || (isDark ? '0.72' : '0.78'),
        '--glass-modal-opacity': td.glassModalOpacity || (isDark ? '0.52' : '0.62'),
        '--glass-modal-shadow': isDark ? '0.35' : '0.12',

        // Focus rings
        '--focus-ring': `0 0 0 2px ${rgba(focusColor, isDark ? 0.40 : 0.25)}`,
        '--focus-ring-error': `0 0 0 2px ${rgba(error, isDark ? 0.40 : 0.25)}`,

        // ===================
        // Theme-specific tokens
        // ===================
        '--font-family': td.fontFamily,
        '--font-family-heading': td.fontFamilyHeading || td.fontFamily,
        '--font-family-display': td.fontFamilyDisplay || td.fontFamilyHeading || td.fontFamily,
        '--font-family-mono': td.fontFamilyMono || 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        '--color-secondary': td.colorSecondary,
        '--color-accent': td.colorAccent,
        '--card-border': td.cardBorder,
        '--card-shadow': td.cardShadow,
        '--color-bg-sidebar': td.colorBgSidebar,

        // Shared translucent application chrome recipe
        '--chrome-background': chromeBackgroundValue,
        '--chrome-background-rgb': chromeBackgroundRgb,
        '--chrome-border': chromeBorder,
        '--chrome-shadow': chromeShadow,
        '--chrome-blur': '24px',
        '--chrome-saturation': '180%',

        // Sidebar geometry and interaction
        '--sidebar-inset': '16px',
        '--sidebar-rail-width': '72px',
        '--sidebar-expanded-width': '280px',
        '--sidebar-expanded-width-compact': '256px',
        '--sidebar-expanded-width-wide': '304px',
        '--sidebar-item-hit-size': '44px',
        '--sidebar-control-size': '44px',
        '--sidebar-primary-icon-size': '20px',
        '--sidebar-secondary-icon-size': '16px',
        '--sidebar-group-indicator-size': '14px',
        '--sidebar-group-icon-size': '10px',
        '--sidebar-active-indicator-width': '3px',
        '--sidebar-flyout-width': '280px',
        '--sidebar-flyout-gap': '8px',
        '--sidebar-flyout-enter-scale': '0.98',
        '--sidebar-viewport-gutter': '32px',
        '--sidebar-z-index': '200',
        '--sidebar-flyout-z-index': '240',

        // ===================
        // Insurance-specific (legacy)
        // ===================

        // Quote status
        '--colorQuoteNew': pick('colorQuoteNew'),
        '--colorQuoteReferred': pick('colorQuoteReferred'),
        '--colorQuoteApproved': pick('colorQuoteApproved'),
        '--colorQuoteDeclined': pick('colorQuoteDeclined'),
        '--colorQuoteConverted': pick('colorQuoteConverted'),
        '--colorQuoteApprovedAlt': pick('colorQuoteApprovedAlt', pick('colorSuccessHover', pick('colorQuoteApproved'))),
        '--colorQuoteNewFocusShadow': rgba(pick('colorQuoteNew', info), 0.2),

        // Policy status
        '--colorPolicyActive': pick('colorPolicyActive'),
        '--colorPolicyLapsed': pick('colorPolicyLapsed'),
        '--colorPolicyCancelled': pick('colorPolicyCancelled'),

        // Claim status
        '--colorClaimOpen': pick('colorClaimOpen'),
        '--colorClaimSettled': pick('colorClaimSettled'),
        '--colorClaimRepudiated': pick('colorClaimRepudiated'),

        // Category badges (Products)
        '--colorCategoryComprehensiveBg': '#fff7e6',
        '--colorCategoryComprehensiveText': '#d46b08',
        '--colorCategoryComprehensiveBorder': '#ffd591',
        '--colorCategoryThirdPartyBg': '#e6f7ff',
        '--colorCategoryThirdPartyText': '#0958d9',
        '--colorCategoryThirdPartyBorder': '#91d5ff',
        '--colorCategoryFireTheftBg': '#f0f5ff',
        '--colorCategoryFireTheftText': '#2f54eb',
        '--colorCategoryFireTheftBorder': '#adc6ff',
        '--colorCategoryComprehensiveBgDark': '#451a03',
        '--colorCategoryComprehensiveTextDark': '#ffa940',
        '--colorCategoryComprehensiveBorderDark': '#873800',
        '--colorCategoryThirdPartyBgDark': '#111d2c',
        '--colorCategoryThirdPartyTextDark': '#69c0ff',
        '--colorCategoryThirdPartyBorderDark': '#15395b',
        '--colorCategoryFireTheftBgDark': '#131629',
        '--colorCategoryFireTheftTextDark': '#85a5ff',
        '--colorCategoryFireTheftBorderDark': '#1d39c4',

        // Status tag semantic (Products)
        '--colorStatusSuccessBg': '#f6ffed',
        '--colorStatusSuccessBorder': '#b7eb8f',
        '--colorStatusSuccessText': pick('colorSuccessText', pick('colorQuoteApproved')),
        '--colorStatusWarningBg': '#fffbe6',
        '--colorStatusWarningBorder': '#ffe58f',
        '--colorStatusWarningText': pick('colorWarningText', pick('colorQuoteReferred')),
        '--colorStatusErrorBg': '#fff2e8',
        '--colorStatusErrorBorder': '#ffbb96',
        '--colorStatusErrorText': pick('colorErrorText', pick('colorQuoteDeclined')),

        // Gradients (Quote details)
        '--colorGradientQuotePrimaryStart': primary,
        '--colorGradientQuotePrimaryEnd': td.colorAccent,
        '--colorGradientQuotePrimaryHoverStart': primaryHover,
        '--colorGradientQuotePrimaryHoverEnd': td.colorAccent,
        '--colorGradientQuotePrimaryStartDark': primaryActive,
        '--colorGradientQuotePrimaryEndDark': td.colorAccent,

        // Generic surfaces / print fallbacks
        '--colorBgPrint': '#ffffff',
        '--colorTextPrint': '#000000',
        '--colorBorderPrint': '#d9d9d9',

        // Shared neutral / secondary
        '--colorTextSecondary': pick('colorTextSecondary', td.colorTextSecondary),
        '--colorBorder': pick('colorBorder', td.colorBorder),
        '--colorFillSecondary': pick('colorFillSecondary', rgba(td.colorText, 0.05)),
        '--colorSuccessStrong': pick('colorSuccessText', success),
        '--colorErrorStrong': pick('colorErrorText', error)
    };
}

const parseHexToRgb = (hex?: string): string | undefined => {
    if (!hex) return;
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return;
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    return `${r}, ${g}, ${b}`;
};

export const DesignSystemThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { theme, currentTheme } = useTheme();
    const isDarkMode = theme === 'dark';

    const themedBase: ThemeConfig = useMemo(() => createThemeConfig(currentTheme), [currentTheme]);

    const mergedTheme: ThemeConfig = useMemo(
        () => ({
            ...mergeWithKoodookaTheme(themedBase, isDarkMode),
            algorithm: isDarkMode ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm
        }),
        [themedBase, isDarkMode]
    );

    // Inject / update CSS custom properties on theme change
    useEffect(() => {
        const tokens = (mergedTheme.token || {}) as TokenMap;
        const varMap = buildCssVarMap(tokens, isDarkMode, currentTheme);
        const root = document.documentElement;

        Object.entries(varMap).forEach(([k, v]) => {
            if (v) root.style.setProperty(k, v);
        });

        // Provide RGB variants where needed (e.g., colorQuoteNewRGB) for rgba() usage
        const quoteNewRgb = parseHexToRgb(tokenString(tokens, 'colorQuoteNew'));
        if (quoteNewRgb) {
            root.style.setProperty('--colorQuoteNewRGB', quoteNewRgb);
        }
    }, [mergedTheme, isDarkMode, currentTheme]);

    return <ConfigProvider theme={mergedTheme}>{children}</ConfigProvider>;
};

export default DesignSystemThemeProvider;
export const useDesignTokens = () => {
    const { theme, currentTheme } = useTheme();
    const isDarkMode = theme === 'dark';
    const themedBase = createThemeConfig(currentTheme);
    const merged = mergeWithKoodookaTheme(themedBase, isDarkMode);
    return merged.token || {};
};
